import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Client } from '../../../context/ClientContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faCheck } from '@fortawesome/free-solid-svg-icons';
import MediaHeader from './media/MediaHeader.tsx';
import MediaGrid from './media/MediaGrid.tsx';
import MediaPreviewModal from './media/MediaPreviewModal.tsx';
import MediaConfigModal from './media/MediaConfigModal.tsx';
import ImageUrlModal from './media/ImageUrlModal.tsx';
import { MediaFile, checkBucketExists, fetchMediaFiles } from './media/mediaUtils';
import { Button, Card } from '../../ui';

interface MediaSectionProps {
  client?: Client;
  expandedSection?: string | null;
  toggleSection?: (section: string) => void;
  onMediaAdded?: () => void;
}

export default function MediaSection({ client, onMediaAdded }: MediaSectionProps) {
  const { clientId: urlClientId } = useParams<{ clientId: string }>();

  // Use client.id if available, otherwise fall back to URL params
  const clientId = client?.id || urlClientId;

  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [bucketExists, setBucketExists] = useState<boolean | null>(null);
  const [isCheckingBucket, setIsCheckingBucket] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isImageUrlModalOpen, setIsImageUrlModalOpen] = useState(false);

  const loadMediaFiles = useCallback(async () => {
    if (!client?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const files = await fetchMediaFiles(client.id);
      setMediaFiles(files);
    } catch (err) {
      console.error('Error fetching media files:', err);
      setError('Failed to load media files');
    } finally {
      setIsLoading(false);
    }
  }, [client?.id]);

  useEffect(() => {
    const initializeMediaSection = async () => {
      if (!client) {
        setIsLoading(false);
        setIsCheckingBucket(false);
        return;
      }

      // First check if bucket exists
      setIsCheckingBucket(true);
      const bucketCheck = await checkBucketExists(client);
      setBucketExists(bucketCheck);
      setIsCheckingBucket(false);

      // If bucket exists, fetch media files
      if (bucketCheck) {
        await loadMediaFiles();
      } else {
        setIsLoading(false);
      }
    };

    initializeMediaSection();
  }, [client, loadMediaFiles]);

  const handleMediaAdded = async () => {
    await loadMediaFiles();
    if (onMediaAdded) {
      onMediaAdded();
    }
  };

  const handlePreview = (file: MediaFile) => {
    setSelectedMedia(file);
    setIsPreviewOpen(true);
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
  };

  // Show bucket setup message if no bucket exists
  if (isCheckingBucket) {
    return (
      <Card eyebrow="Media" title="Checking storage...">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <div
            className="animate-spin w-7 h-7 border-2 rounded-full"
            style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
          />
        </div>
      </Card>
    );
  }

  if (bucketExists === false) {
    return (
      <Card eyebrow="Media" title="Storage Not Configured">
        <div style={{ padding: '12px 0 4px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <FontAwesomeIcon icon={faImage} style={{ fontSize: '22px', color: 'var(--text-faint)' }} />
          <p style={{ margin: '12px auto 16px', maxWidth: '420px', fontSize: 'var(--text-sm)' }}>
            This client does not have media storage configured yet. Set up a Cloudflare R2 bucket with a custom
            domain to enable uploads.
          </p>
          <div
            style={{
              maxWidth: '460px',
              margin: '0 auto 18px',
              padding: '14px 16px',
              textAlign: 'left',
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div className="t-eyebrow" style={{ marginBottom: '8px' }}>Setup</div>
            <ol
              style={{
                margin: 0,
                paddingLeft: '18px',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                lineHeight: 1.7,
              }}
            >
              <li>
                Create a Cloudflare R2 bucket named{' '}
                <code className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--accent-text)' }}>
                  {client?.id}
                </code>
              </li>
              <li>Set up a custom domain for the bucket (e.g. media.clientdomain.com)</li>
              <li>Configure CORS policies to allow your application domain</li>
              <li>Update this client&apos;s media domain in the database</li>
              <li>Ensure your Netlify environment variables are configured</li>
            </ol>
            <p className="t-num" style={{ margin: '10px 0 0', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
              Bucket name must exactly match the client ID shown above.
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setIsConfigModalOpen(true)}>
            Add Domain
          </Button>
        </div>

        {/* Media Domain Configuration Panel */}
        <MediaConfigModal
          isOpen={isConfigModalOpen}
          onClose={() => setIsConfigModalOpen(false)}
          client={client}
          onSuccess={showSuccess}
          onError={showError}
        />
      </Card>
    );
  }

  return (
    <Card
      eyebrow="Media"
      title={mediaFiles.length > 0 ? `${mediaFiles.length} ${mediaFiles.length === 1 ? 'file' : 'files'}` : 'No media yet'}
      actions={
        <MediaHeader
          onFileUpload={handleMediaAdded}
          onImageUrlClick={() => setIsImageUrlModalOpen(true)}
          onError={showError}
          onSuccess={showSuccess}
          clientId={clientId}
        />
      }
    >
      {/* Status Messages */}
      {error && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="hover-underline"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: 'var(--text-xs)',
              flexShrink: 0,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: '14px',
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-up-soft)',
            border: '1px solid rgba(86, 162, 104, 0.25)',
            color: 'var(--status-up)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <FontAwesomeIcon icon={faCheck} style={{ fontSize: '11px', marginRight: '8px' }} />
          {success}
        </div>
      )}

      <MediaGrid
        mediaFiles={mediaFiles}
        isLoading={isLoading}
        onPreview={handlePreview}
        onMediaAdded={handleMediaAdded}
        onError={showError}
        onSuccess={showSuccess}
        client={client}
      />

      {/* File Upload Guidelines */}
      <p
        style={{
          margin: '18px 0 0',
          paddingTop: '14px',
          borderTop: '1px solid var(--border-hairline)',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-faint)',
        }}
      >
        Supported media: images (JPEG, PNG, GIF, WebP, SVG), video (MP4, WebM, MOV, AVI), audio (MP3, WAV, OGG,
        M4A), PDF. Max file size 50 MB.
      </p>

      {/* Media Preview Modal */}
      <MediaPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        selectedMedia={selectedMedia}
        client={client}
      />

      {/* Media Domain Configuration Panel */}
      <MediaConfigModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        client={client}
        onSuccess={showSuccess}
        onError={showError}
      />

      {/* Image URL Panel */}
      <ImageUrlModal
        isOpen={isImageUrlModalOpen}
        onClose={() => setIsImageUrlModalOpen(false)}
        client={client}
        onSuccess={(message) => {
          showSuccess(message);
          handleMediaAdded();
        }}
        onError={showError}
      />
    </Card>
  );
}
